import { Helmet } from 'react-helmet-async';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PublicHeader } from '@/components/marketing/PublicHeader';
import { PublicFooter } from '@/components/marketing/PublicFooter';
import { LegalDocumentRenderer } from './LegalDocumentRenderer';
import { extractMainSections, type TocEntry } from '@/lib/legal-utils';
import { useMemo } from 'react';

interface LegalPageMetadata {
  version?: string;
  effectiveDate?: string;
}

interface LegalPageLayoutProps {
  title: string;
  subtitle?: string;
  metadata?: LegalPageMetadata;
  content: string;
  seoDescription: string;
  canonicalPath: string;
}

export function LegalPageLayout({
  title,
  subtitle,
  metadata,
  content,
  seoDescription,
  canonicalPath,
}: LegalPageLayoutProps) {
  // Extract TOC from markdown content
  const sections = useMemo(() => extractMainSections(content), [content]);

  return (
    <>
      <Helmet>
        <title>{subtitle ? `${subtitle} | Radius` : `${title} | Radius`}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={`https://useradius.io${canonicalPath}`} />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <PublicHeader />

        <main className="flex-1">
          {/* Hero Section */}
          <section className="bg-muted/30 border-b">
            <div className="container py-16 md:py-24">
              <div className="max-w-3xl">
                <p className="text-sm font-medium text-primary mb-2">RADIUS TECHNOLOGIES GROUP LLC</p>
                <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
                  {title}
                </h1>
                {metadata && (
                  <div className="space-y-1 text-lg text-muted-foreground">
                    {metadata.version && <p>Version: {metadata.version}</p>}
                    {metadata.effectiveDate && <p>Effective Date: {metadata.effectiveDate}</p>}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Content Section */}
          <section className="container py-12 md:py-16">
            <div className="grid lg:grid-cols-[280px_1fr] gap-12">
              {/* Table of Contents - Sticky Sidebar */}
              <aside className="hidden lg:block">
                <div className="sticky top-24">
                  <h2 className="text-lg font-semibold mb-4">Table of Contents</h2>
                  <ScrollArea className="h-[calc(100vh-180px)]">
                    <nav className="space-y-2 pr-4">
                      {sections.map((section) => (
                        <a
                          key={section.id}
                          href={`#${section.id}`}
                          className="block text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
                        >
                          {section.title}
                        </a>
                      ))}
                    </nav>
                  </ScrollArea>
                </div>
              </aside>

              {/* Main Content */}
              <div>
                {/* Mobile Table of Contents */}
                <div className="lg:hidden mb-8 p-4 bg-muted/50 rounded-lg">
                  <h2 className="text-lg font-semibold mb-3">Table of Contents</h2>
                  <nav className="space-y-1">
                    {sections.map((section) => (
                      <a
                        key={section.id}
                        href={`#${section.id}`}
                        className="block text-sm text-muted-foreground hover:text-foreground transition-colors py-0.5"
                      >
                        {section.title}
                      </a>
                    ))}
                  </nav>
                </div>

                {/* Document Content */}
                <LegalDocumentRenderer content={content} />
              </div>
            </div>
          </section>
        </main>

        <PublicFooter />
      </div>
    </>
  );
}
